/**
 * CR456: Mapping validation helper.
 * Validates a FLYER source mapping against its adapter contract.
 * CR457: Replaced bare Record generics with JsonObject alias.
 */

type JsonObject = Record<string, unknown>;

export type ValidationIssue = {
  level: "ERROR" | "WARN";
  code: string;
  message: string;
  field?: string;
};

export type ValidationResult = {
  health: "OK" | "WARN" | "ERROR";
  issues: ValidationIssue[];
};

export interface FlyerSourceRow {
  merchant_source_id: string;
  merchant_id: string;
  merchant_name: string | null;
  scan_type: string;
  status: string;
  source_url: string | null;
  resolved_flyer_url_template: string | null;
  requires_region: boolean | null;
  region_strategy: string | null;
  default_postal_code: string | null;
  region_cookie_payload: unknown | null;
  region_resolution_timeout_ms: number | null;
  flyer_image_capture_enabled: boolean | null;
  flyer_image_capture_adapter_key: string | null;
  flyer_image_capture_params: unknown | null;
  adapter_enabled: boolean | null;
  adapter_display_name: string | null;
  entrypoint: string | null;
  version: string | null;
  default_timeout_ms: number | null;
  param_schema: unknown | null;
  default_params: unknown | null;
  required_context: string[] | null;
  adapter_notes: string | null;
}

function resolvePath(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    const rec = cur as JsonObject;
    cur = rec[p];
  }
  return cur;
}

function buildCtxView(source: FlyerSourceRow): JsonObject {
  return {
    urls: {
      source_url: source.source_url ?? null,
      resolved_flyer_url_template: source.resolved_flyer_url_template ?? null,
    },
    region: {
      requires_region: !!source.requires_region,
      strategy: source.region_strategy ?? null,
      postal_code: source.default_postal_code ?? null,
      cookie_payload: source.region_cookie_payload ?? null,
      resolution_timeout_ms: source.region_resolution_timeout_ms ?? null,
    },
    limits: {
      max_pages: null,
      timeout_ms: source.default_timeout_ms ?? null,
    },
    adapter_params: source.flyer_image_capture_params ?? null,
  };
}

/**
 * Minimal JSON schema required-field checker.
 * Validates that all properties marked "required" in param_schema are present in params.
 */
function validateParamSchema(
  schema: unknown,
  params: unknown
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!schema || typeof schema !== "object") return issues;
  const s = schema as JsonObject;
  const required = Array.isArray(s["required"]) ? (s["required"] as string[]) : [];
  const paramObj = params && typeof params === "object" ? (params as JsonObject) : ({} as JsonObject);

  for (const field of required) {
    if (!(field in paramObj) || paramObj[field] == null || paramObj[field] === "") {
      issues.push({
        level: "ERROR",
        code: "PARAM_MISSING_REQUIRED",
        message: `Required adapter param "${field}" is missing or empty.`,
        field,
      });
    }
  }
  return issues;
}

export function validateMapping(source: FlyerSourceRow): ValidationResult {
  const issues: ValidationIssue[] = [];

  // 1. scan_type must be FLYER
  if (source.scan_type !== "FLYER") {
    issues.push({
      level: "ERROR",
      code: "NOT_FLYER_SOURCE",
      message: `scan_type is "${source.scan_type}", must be FLYER.`,
      field: "scan_type",
    });
    return { health: "ERROR", issues };
  }

  // 2. capture enabled
  if (!source.flyer_image_capture_enabled) {
    issues.push({
      level: "WARN",
      code: "CAPTURE_NOT_ENABLED",
      message: "flyer_image_capture_enabled is false. Enable it to use image capture.",
      field: "flyer_image_capture_enabled",
    });
  }

  // 3. adapter key set
  if (!source.flyer_image_capture_adapter_key) {
    issues.push({
      level: "ERROR",
      code: "NO_ADAPTER_KEY",
      message: "No adapter is mapped to this source. Set flyer_image_capture_adapter_key.",
      field: "flyer_image_capture_adapter_key",
    });
    return { health: "ERROR", issues };
  }

  // 4. adapter enabled
  if (source.adapter_enabled === false) {
    issues.push({
      level: "ERROR",
      code: "ADAPTER_DISABLED",
      message: `Adapter "${source.flyer_image_capture_adapter_key}" exists but is disabled.`,
      field: "adapter_enabled",
    });
  }

  // 5. adapter found at all
  if (source.adapter_enabled === null) {
    issues.push({
      level: "ERROR",
      code: "ADAPTER_NOT_FOUND",
      message: `Adapter key "${source.flyer_image_capture_adapter_key}" is not registered in the adapter registry.`,
      field: "flyer_image_capture_adapter_key",
    });
    return { health: "ERROR", issues };
  }

  // 6. required_context paths exist
  const requiredCtx = Array.isArray(source.required_context) ? source.required_context : [];
  const ctxView = buildCtxView(source);
  for (const ctxPath of requiredCtx) {
    const val = resolvePath(ctxView, ctxPath);
    if (val == null || val === "") {
      issues.push({
        level: "WARN",
        code: "MISSING_CTX_VALUE",
        message: `Required context path "${ctxPath}" is missing or empty for this source.`,
        field: ctxPath,
      });
    }
  }

  // 7. param_schema validation
  if (source.param_schema) {
    const schemaIssues = validateParamSchema(
      source.param_schema,
      source.flyer_image_capture_params
    );
    issues.push(...schemaIssues);
  }

  // 8. source_url required
  if (!source.source_url) {
    issues.push({
      level: "WARN",
      code: "MISSING_SOURCE_URL",
      message: "source_url is empty. The adapter may not be able to navigate to the flyer.",
      field: "source_url",
    });
  }

  // 9. requires_region but no postal code
  if (source.requires_region && !source.default_postal_code) {
    issues.push({
      level: "WARN",
      code: "MISSING_POSTAL_CODE",
      message: "requires_region is true but default_postal_code is not set.",
      field: "default_postal_code",
    });
  }

  const hasError = issues.some((i) => i.level === "ERROR");
  const hasWarn = issues.some((i) => i.level === "WARN");

  return {
    health: hasError ? "ERROR" : hasWarn ? "WARN" : "OK",
    issues,
  };
}

/** Generate best-effort fix suggestions as SQL snippets */
export function generateFixSuggestions(source: FlyerSourceRow, result: ValidationResult): string[] {
  const suggestions: string[] = [];

  for (const issue of result.issues) {
    switch (issue.code) {
      case "CAPTURE_NOT_ENABLED":
        suggestions.push(
          `UPDATE merchant_sources SET flyer_image_capture_enabled = true WHERE id = '${source.merchant_source_id}';`
        );
        break;
      case "NO_ADAPTER_KEY":
        suggestions.push(
          `-- Set adapter key: UPDATE merchant_sources SET flyer_image_capture_adapter_key = '<adapter_key>' WHERE id = '${source.merchant_source_id}';`
        );
        break;
      case "ADAPTER_DISABLED":
        suggestions.push(
          `UPDATE flyer_image_capture_adapters SET enabled = true WHERE adapter_key = '${source.flyer_image_capture_adapter_key}';`
        );
        break;
      case "MISSING_POSTAL_CODE":
        suggestions.push(
          `UPDATE merchant_sources SET default_postal_code = '<POSTAL_CODE>' WHERE id = '${source.merchant_source_id}';`
        );
        break;
    }
  }

  return [...new Set(suggestions)];
}
